/**
 * Shared API auth helpers (task_111).
 *
 * Centralizes how outbound API calls authenticate so both providers work:
 * - Firebase: `getToken()` returns the ID token → attach `Authorization:
 *   Bearer <token>`.
 * - Studio:   `getToken()` returns null → omit `Authorization`; the HttpOnly
 *   `gml_sso` cookie carries auth and travels because we always send
 *   `credentials: 'include'`.
 *
 * Every authenticated fetch should spread `AUTH_FETCH_INIT` (or pass
 * `credentials: 'include'`) AND build headers via `authHeaders()`.
 */

import { getAuthProvider } from './authProviderFactory';

/** Build request headers, attaching a Bearer token only when one exists. */
export async function authHeaders(
  extra: Record<string, string> = {}
): Promise<Record<string, string>> {
  const token = await getAuthProvider().getToken();
  const headers: Record<string, string> = { ...extra };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/** JSON headers + Bearer (when present). Convenience for JSON POST/PUT bodies. */
export async function jsonAuthHeaders(
  extra: Record<string, string> = {}
): Promise<Record<string, string>> {
  return authHeaders({ 'Content-Type': 'application/json', ...extra });
}

/**
 * Spread into every authenticated `fetch` init so the Studio session cookie
 * is sent cross-subdomain. Harmless on the same-origin Firebase path.
 */
export const AUTH_FETCH_INIT: Pick<RequestInit, 'credentials'> = {
  credentials: 'include',
};
